import { SideMenuNav, PrivateComponent, LoginSettings, PublicSite, Modal, NotFound, WindowLoader, ErrorBoundary, AppMenuItem, useAuthService } from 'medere-ui-framework'
import { Route, Routes, useNavigate } from 'react-router'
import React, { Suspense, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { version } from '../../../package.json'
import { routeComponents } from "../../utils/Constants";
import VaadinView from '../../components/Vaadin/VaadinView';
import ProfileDataFormView from '../Profile/Views/ProfileDataFormView';
import PatientCallerWidget from '../Location/TotemWidgets/PatientCallerWidget';
import { hasActiveAssociatedDeviceToHospitalLocation } from '../../servicesHooks/Location/useLocationConfigService';

function renderRouteComponent(Component: React.ComponentType<any>) {
  return <Component />;
}

export default function Dashboard() {
  const intl = useIntl();
  const navigate = useNavigate();
  const { handleSignOut } = useAuthService();

  const [appConfig] = useState<LoginSettings>(() => JSON.parse(localStorage.getItem('settings') ?? "{}"));
  const [siteSettings] = useState<PublicSite>(() => JSON.parse(localStorage.getItem('siteSettings') ?? "{}"));

  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [hasActiveAssociatedDevice, setHasActiveAssociatedDevice] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState(false);
  const [openCaller, setOpenCaller] = useState(false);

  useEffect(() => {
    hasActiveAssociatedDeviceToHospitalLocation(localStorage.getItem("uuid")).then((res) => {
      setHasActiveAssociatedDevice(res);
    });
  }, []);

  useEffect(() => {
    let objectUrl: string | null = null;

    fetch("/api/public/site/getImage")
      .then((response) => {
        if (!response.ok) {
          throw new Error("Network response was not ok");
        }
        return response.blob();
      })
      .then((blob) => {
        objectUrl = URL.createObjectURL(blob);
        setLogoUrl(objectUrl);
      })
      .catch((error) => {
        console.error("Error fetching the logo image:", error);
      });

    return () => {
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, []);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const userMenuItems: AppMenuItem[] = useMemo(() => [
    {
      icon: 'pi pi-cog',
      caption: 'Mi perfil',
      route: '/ProfileDataFormView',
      onclick: () => { },
      description: "",
      menuItem: -1,
      element: "",
      order: -1,
      itemsActions: [],
      items: []
    },
    {
      icon: 'pi pi-question',
      caption: 'Ayuda',
      route: "",
      onclick: () => window.open("https://advenio.atlassian.net/servicedesk/customer/portal/3", "_blank"),
      description: "",
      menuItem: -1,
      element: "",
      order: -1,
      itemsActions: [],
      items: []
    }
  ], []);

  if (!localStorage.getItem('settings')) return <></>;

  return (
    <SideMenuNav
      showMenu
      version={version}
      pathLogo={logoUrl ?? "/imgs/defaultlogo.png"}
      logoAlt={siteSettings.appTitle}
      logoHeight={45}
      profileImgPath="/imgs/perfil.png"
      loginResponse={appConfig.loginResponse}
      handleSignOut={() => setConfirmDialog(true)}
      title={siteSettings.appTitle}
      appPath='/imgs/app.png'
      appAlt='Medere'
      appHeight={32}
      dividerVersion
      showChangeDataWindow={false}
      additionalUserMenuItems={userMenuItems}
      dashboardContainerClassname='bg-white'
    >
      <ErrorBoundary>
        <Suspense fallback={<WindowLoader showLoader />}>
          <Routes>
            <Route element={<PrivateComponent />}>
              {appConfig.loginResponse.appMenuConfiguration.menuItems.map((menuItem, index) => {
                const routeItem = routeComponents.find(item => item.name === menuItem.element);

                if (routeItem) {
                  const key = index;
                  return (
                    <Route
                      key={`menu-${key}`}
                      path={routeItem.route}
                      element={renderRouteComponent(routeItem.component)}
                    />
                  );
                }

                return menuItem.items.map((subMenuItem, subIndex) => {
                  if (subMenuItem.vaadinView) {
                    return (
                      <Route
                        key={`menu-${index}-sub-${subIndex}`}
                        path={`/${subMenuItem.route ?? 'notFound'}`}
                        element={<VaadinView route={subMenuItem.route ?? 'notFound'} name={subMenuItem.caption} />}
                      />
                    );
                  }

                  const subRouteItem = routeComponents.find(item => item.name === subMenuItem.element);
                  return subRouteItem ? (
                    <Route
                      key={`menu-${index}-sub-${subIndex}`}
                      path={subRouteItem.route}
                      element={renderRouteComponent(subRouteItem.component)}
                    />
                  ) : null;
                });
              })}

              <Route path='/ProfileDataFormView' element={<ProfileDataFormView />} />
            </Route>
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </ErrorBoundary>

      {!isMobile && hasActiveAssociatedDevice && (
        <>
          {openCaller && <div className="caller-overlay" onClick={() => setOpenCaller(false)} />}
          <PatientCallerWidget open={openCaller} setOpen={setOpenCaller} />
        </>
      )}

      <Modal
        visible={confirmDialog}
        setVisible={setConfirmDialog}
        width='w-medium'
        header={<p className='info-dark text-3xl'>Estás por cerrar tu sesión.</p>}
        footerButtonRightText={"Sí, quiero cerrar"}
        footerButtonLeftText={intl.formatMessage({ id: "Cancel" })}
        onClickRightBtn={() => handleSignOut().then(() => navigate('/login'))}
        onClickLeftBtn={() => setConfirmDialog(false)}
      />
    </SideMenuNav>
  );
}
